import { memoize } from "es-toolkit";
import { generatePath } from "react-router";

import type { GetNodeTypeIdById } from "@skilitics/nautilus-core-designer-data";

import type { NodeRouting } from "../../types/index.ts";
import type { GetParentContextById } from "../getNodeParentContext.ts";
import type { GetNodeAncestorIds } from "./getNodeAncestorIds.ts";

export type GetNodeDesignerRouting = (typeId: string) => NodeRouting<any> | undefined;

interface CreateOptions {
  getAncestorIds: GetNodeAncestorIds;
  getTypeId: GetNodeTypeIdById;
  getRouting: GetNodeDesignerRouting;
  getParentContext: GetParentContextById;
  getNodeIdExists: (id: number) => boolean;
}

export const getNodeRoutePathCreate = ({
  getTypeId,
  getRouting,
  getAncestorIds,
  getParentContext,
  getNodeIdExists,
}: CreateOptions): GetNodeRoutePath =>
  // NOTE: memoize will ignore changes in parent context. so far all uses of parent context are stable (i.e. nest level)
  memoize((nodeId) => {
    // A published cycle can name a node that only exists once published, which the design has no
    // type for, let alone a route to it.
    if (!getNodeIdExists(nodeId)) {
      return "";
    }

    const nodeIds = getAncestorIds(nodeId).reverse().concat(nodeId);
    const segments = nodeIds
      .map((id): [number, NodeRouting | undefined] => [id, getRouting(getTypeId(id))])
      .filter((pair): pair is [number, NodeRouting] => !!pair[1]);

    return segments
      .map(([id, routing]) => {
        const segment =
          typeof routing.segment === "function"
            ? routing.segment({ parentContext: getParentContext(id) })
            : routing.segment;
        if (!segment) {
          return undefined;
        }
        return generatePath(segment, { id: id.toString() });
      })
      .filter((s) => s !== undefined)
      .join("/");
  });

export type GetNodeRoutePath = (id: number) => string;
